import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import type { AboutPageData } from '../../types/api';

export const AboutPage: React.FC = () => {
    const [data, setData] = useState<AboutPageData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        api.getAbout().then(res => {
            if (res.data) setData(res.data);
        }).catch(() => {}).finally(() => {
            setLoading(false);
        });
    }, []);

    const settings = data?.settings || {};
    const profilePhotoUrl = settings.profile_photo ? `/storage/${settings.profile_photo}` : "/rakib image.jpeg";

    return (
        <div>
            {/* About Header Banner */}
            <section className="relative py-20 bg-radial-glow border-b border-slate-800/80">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl space-y-4">
                        <span className="text-xs font-bold text-emerald-400 tracking-wider uppercase">Biography & Vision</span>
                        <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
                            About Rakib Hasan
                        </h1>
                        <p className="text-lg text-slate-300 leading-relaxed">
                            Dedicated student leader, organizer, and community volunteer committed to structured youth leadership and meaningful civic action in Bangladesh.
                        </p>
                    </div>
                </div>
            </section>

            {/* Biography Main Content */}
            <section className="py-20 bg-slate-950">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        
                        {/* Left: Portrait Card & Key Facts */}
                        <div className="lg:col-span-5 space-y-8">
                            <div className="relative bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden p-3 shadow-2xl">
                                <img src={profilePhotoUrl} alt="Rakib Hasan" className="w-full aspect-[4/5] object-cover object-top rounded-2xl" />
                                
                                <div className="p-4 space-y-2 text-center">
                                    <h2 className="text-xl font-bold text-white">Rakib Hasan</h2>
                                    <p className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Rakib's Care</p>
                                    <p className="text-xs text-slate-400">Habiganj, Sylhet, Bangladesh</p>
                                </div>
                            </div>

                            {/* Verified Affiliations Card */}
                            <div className="p-6 rounded-2xl glass-card space-y-4">
                                <h3 className="text-sm font-bold text-white uppercase tracking-wider">Verified Executive Roles</h3>
                                <ul className="space-y-3 text-sm text-slate-300">
                                    <li className="flex items-start gap-3">
                                        <span className="text-emerald-400 mt-0.5">✔</span>
                                        <div>
                                            <span className="font-bold text-white block">President</span>
                                            <span className="text-xs text-slate-400">English Knowledge Holders (2024–2025)</span>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-emerald-400 mt-0.5">✔</span>
                                        <div>
                                            <span className="font-bold text-white block">Treasurer</span>
                                            <span className="text-xs text-slate-400">Interact Club of Habiganj (2024–2026)</span>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-emerald-400 mt-0.5">✔</span>
                                        <div>
                                            <span className="font-bold text-white block">1st Leader</span>
                                            <span className="text-xs text-slate-400">Habiganj Govt. High School Band Team (2023–2024)</span>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-emerald-400 mt-0.5">✔</span>
                                        <div>
                                            <span className="font-bold text-white block">Charity Partner</span>
                                            <span className="text-xs text-slate-400">Magico'24</span>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-emerald-400 mt-0.5">✔</span>
                                        <div>
                                            <span className="font-bold text-white block">Treasurer</span>
                                            <span className="text-xs text-slate-400">Muktanchol (2022–2023)</span>
                                        </div>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-emerald-400 mt-0.5">✔</span>
                                        <div>
                                            <span className="font-bold text-white block">Active Member</span>
                                            <span className="text-xs text-slate-400">HMBC</span>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* Right: Biography, Story & Values */}
                        <div className="lg:col-span-7 space-y-10">
                            {/* Personal Narrative */}
                            <div className="space-y-4">
                                <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">The Story</span>
                                <h2 className="text-2xl sm:text-3xl font-bold text-white">Youth Engagement Through Purposeful Action</h2>
                                <div className="text-slate-300 leading-relaxed space-y-4 text-base whitespace-pre-line">
                                    {settings.bio_full || 'Rakib Hasan combines structured management with active community dedication. Serving across executive student bodies, charity coordination, and school ceremonial teams, he brings focused momentum to youth initiatives.'}
                                </div>
                            </div>

                            {/* Leadership Philosophy Card */}
                            <div className="p-8 rounded-3xl bg-slate-900 border border-emerald-500/30 space-y-4 relative overflow-hidden">
                                <div className="absolute -right-6 -bottom-6 w-36 h-36 bg-emerald-500/10 rounded-full blur-2xl pointer-events-none"></div>
                                <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">Leadership Philosophy</span>
                                <blockquote className="text-xl font-medium text-white italic leading-relaxed">
                                    "{settings.leadership_philosophy || 'True leadership is rooted in disciplined service, empathy for your team, and the courage to organize collective energy toward meaningful civic impact.'}"
                                </blockquote>
                                <p className="text-xs text-slate-400">— Rakib Hasan</p>
                            </div>

                            {/* Verified Impact Pillars */}
                            <div className="space-y-4">
                                <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">Focus Areas</span>
                                <h2 className="text-2xl font-bold text-white">Areas of Active Dedication</h2>
                                
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                                    <div className="p-5 rounded-2xl glass-card space-y-2">
                                        <h3 className="text-base font-bold text-white">Educational Enrichment</h3>
                                        <p className="text-xs text-slate-400 leading-relaxed">Organizing interactive academic forums and confidence-building workshops for students.</p>
                                    </div>
                                    <div className="p-5 rounded-2xl glass-card space-y-2">
                                        <h3 className="text-base font-bold text-white">Fiscal Transparency</h3>
                                        <p className="text-xs text-slate-400 leading-relaxed">Maintaining clear financial bookkeeping and transparent budgeting in social organizations.</p>
                                    </div>
                                    <div className="p-5 rounded-2xl glass-card space-y-2">
                                        <h3 className="text-base font-bold text-white">Ceremonial Leadership</h3>
                                        <p className="text-xs text-slate-400 leading-relaxed">Cultivating group rhythm, discipline, and synchronized drill formations in school ensembles.</p>
                                    </div>
                                    <div className="p-5 rounded-2xl glass-card space-y-2">
                                        <h3 className="text-base font-bold text-white">Civic Benevolence</h3>
                                        <p className="text-xs text-slate-400 leading-relaxed">Partnering with humanitarian initiatives to deliver tangible community support.</p>
                                    </div>
                                </div>
                            </div>

                            {/* Call to Action */}
                            <div className="pt-6 flex items-center gap-4">
                                <Link to="/leadership" className="px-6 py-3 rounded-full text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 text-white transition">
                                    Explore Full Activities Timeline &rarr;
                                </Link>
                                <Link to="/contact" className="px-6 py-3 rounded-full text-sm font-semibold bg-slate-900 border border-slate-700 hover:border-slate-500 text-slate-200 transition">
                                    Send a Message
                                </Link>
                            </div>

                        </div>

                    </div>
                </div>
            </section>
        </div>
    );
};
